import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["initials", "avatarUrl"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useLoginVerifyQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.hooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { getPortalInstanceId, getPortalAccountId } from '../utils/metadata';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function UserAvatar({
  initials,
  avatarUrl,
  size = 'medium'
}) {
  const dimensions = size === 'small' ? 32 : 40;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);
  const showImage = Boolean(avatarUrl) && !imageFailed;
  if (showImage) {
    return /*#__PURE__*/_jsx(Image, {
      src: avatarUrl,
      alt: "",
      shape: "circle",
      width: dimensions,
      height: dimensions,
      bordered: true,
      borderColor: "border-core-default",
      onError: () => setImageFailed(true)
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    backgroundColor: "fill-accent-teal-default",
    borderRadius: "full",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-default",
    inlineSize: `${dimensions}px`,
    blockSize: `${dimensions}px`,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(Text, {
      typography: size === 'small' ? 'detail-100' : 'body-100',
      color: "text-inverse-default",
      children: initials
    })
  });
}
function AccountButton(_ref) {
  let {
      initials,
      avatarUrl
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
    use: "unstyled",
    "aria-label": I18n.text('app.myAccountPopover.ariaLabel'),
    paddingInline: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      children: [/*#__PURE__*/_jsx(UserAvatar, {
        initials: initials,
        avatarUrl: avatarUrl,
        size: "small"
      }), /*#__PURE__*/_jsx(DownIcon, {
        size: "100",
        color: "text-core-default"
      })]
    })
  }));
}
export function MyAccountPopover() {
  const portalInstanceId = getPortalInstanceId();
  const accountId = getPortalAccountId();
  const isPill = useIsPillBorderRadius();
  const {
    data: loginVerifyData,
    loading: loginVerifyLoading
  } = useLoginVerifyQuery({
    variables: {
      accountId,
      portalInstanceId
    }
  });
  const initials = useMemo(() => {
    if (!loginVerifyData) {
      return 'U';
    }
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  const avatarUrl = loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.avatarUrl;
  const renderButton = useCallback(props => /*#__PURE__*/_jsx(AccountButton, Object.assign({}, props, {
    initials: initials,
    avatarUrl: avatarUrl
  })), [initials, avatarUrl]);
  if (loginVerifyLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "extra-small"
    });
  }
  if (!loginVerifyData) {
    return /*#__PURE__*/_jsx(Button, {
      use: "primary",
      size: "small",
      href: `/_hcms/mem/login?redirect_url=${encodeURIComponent(window.location.href)}`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.login"
      })
    });
  }
  return /*#__PURE__*/_jsx(Dropdown, {
    buttonUse: "unstyled",
    menuWidth: 240,
    placement: "bottom left",
    autoPlacement: false,
    Button: renderButton,
    popoverProps: isPill ? {
      borderRadius: '0'
    } : undefined,
    children: /*#__PURE__*/_jsx(View, {
      padding: "300",
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        size: "small",
        href: "/_hcms/mem/logout",
        block: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.logout"
        })
      })
    })
  });
}